/*
 * out-of-stock-buttons.js
 *
 * When the currently-selected variant is out of stock, hide the
 * "Add To Cart" control (orange button + qty input), the Quotify
 * "Request a Formal Quote" button and the legacy .formalquote2 box,
 * show a "Recently Sold" badge in the price area, and show a red
 * out-of-stock message above the Quotify "Show Quote Cart" button.
 *
 * Two drivers:
 *   1. Initial page load  — reads the server-rendered #ms-oos-state flag
 *      (set in snippets/product-accessories-block.liquid). The qty input,
 *      add button and badge are also pre-classed server-side so there is
 *      no flash of the wrong state.
 *   2. Variant changes    — subscribes to Dawn's `variant-change` pub/sub
 *      event (fired by assets/product-info.js) and re-evaluates using the
 *      newly-selected variant's `available` flag.
 *
 * The Quotify quote button is injected asynchronously by the Quotify app,
 * so we re-apply the current state on a short interval to catch it once it
 * appears (and if the app re-renders it).
 */
(function () {
  'use strict';

  var HIDDEN_CLASS = 'ms-oos-hidden';
  var currentlyAvailable = true;

  // Elements that make up the native "Add To Cart" control.
  function getAddToCartEls() {
    return [
      document.getElementById('addon-qty'),
      document.getElementById('addon-add-btn'),
      document.getElementById('ms-warranty-block'),
    ].filter(Boolean);
  }

  // Find the Quotify "Request a Formal Quote" trigger (NOT the quote-cart
  // button). Mirrors snippets/formal-quote-quotify-redirect.liquid so the
  // two stay consistent if Quotify's markup changes.
  function getQuoteButton() {
    var byAttr = document.querySelector(
      '[data-quotify]:not([data-quotify-cart]):not([data-quotify-convert-cart])'
    );
    if (byAttr) return byAttr;

    var candidates = document.querySelectorAll(
      'button, a.button, a.btn, .btn, [role="button"]'
    );
    for (var i = 0; i < candidates.length; i++) {
      var btn = candidates[i];
      var text = (btn.textContent || '').trim().toLowerCase();
      if (text === 'request a formal quote' || text === 'request quote') {
        return btn;
      }
    }
    return null;
  }

  function getBadge() {
    return document.getElementById('ms-oos-badge');
  }

  // Find the Quotify "Show Quote Cart" button (the cart button, NOT the
  // request-quote trigger). The out-of-stock message is positioned above it.
  function getQuoteCartButton() {
    var byAttr = document.querySelector('[data-quotify-cart], [data-quotify-convert-cart]');
    if (byAttr) return byAttr;

    var candidates = document.querySelectorAll(
      'button, a.button, a.btn, .btn, [role="button"]'
    );
    for (var i = 0; i < candidates.length; i++) {
      var btn = candidates[i];
      var text = (btn.textContent || '').trim().toLowerCase();
      if (text.indexOf('quote cart') !== -1) {
        return btn;
      }
    }
    return null;
  }

  // Move the OOS message directly above the Show Quote Cart button (once the
  // app has rendered it) and toggle its visibility.
  function positionMessage(hide) {
    var msg = document.getElementById('ms-oos-message');
    if (!msg) return;

    if (hide) {
      var cartBtn = getQuoteCartButton();
      if (cartBtn) {
        // Anchor on the button's wrapper so the message sits on its own line
        // above the whole button, not inline beside it.
        var anchor = cartBtn.closest('div') || cartBtn;
        if (anchor.parentNode && msg.nextElementSibling !== anchor) {
          anchor.parentNode.insertBefore(msg, anchor);
        }
      }
      msg.classList.remove(HIDDEN_CLASS);
    } else {
      msg.classList.add(HIDDEN_CLASS);
    }
  }

  // Apply the given availability state to all controls.
  function apply(available) {
    var hide = !available;

    getAddToCartEls().forEach(function (el) {
      el.classList.toggle(HIDDEN_CLASS, hide);
    });

    var quote = getQuoteButton();
    if (quote) quote.classList.toggle(HIDDEN_CLASS, hide);

    // Legacy "Request a Formal Quote" box at the bottom of the product
    // description (imported from Volusion, wrapped in div.formalquote2).
    var legacyQuoteBoxes = document.querySelectorAll('.formalquote2');
    for (var i = 0; i < legacyQuoteBoxes.length; i++) {
      legacyQuoteBoxes[i].classList.toggle(HIDDEN_CLASS, hide);
    }

    var badge = getBadge();
    if (badge) badge.classList.toggle(HIDDEN_CLASS, !hide);

    positionMessage(hide);
  }

  function setAvailable(available) {
    currentlyAvailable = !!available;
    apply(currentlyAvailable);
  }

  function init() {
    // 1. Initial state from the server-rendered flag.
    var flag = document.getElementById('ms-oos-state');
    setAvailable(!flag || flag.dataset.available !== 'false');

    // 2. React to variant changes on multi-variant products.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var variant = event && event.data && event.data.variant;
        setAvailable(!!(variant && variant.available));
      });
    }

    // 3. Quotify injects its button asynchronously — keep re-applying the
    //    current state for a few seconds so the quote button is hidden as
    //    soon as it renders. Cheap (just class toggles) and self-stopping.
    var ticks = 0;
    var timer = setInterval(function () {
      apply(currentlyAvailable);
      if (++ticks >= 40) clearInterval(timer); // ~10s at 250ms
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
